import { FormikProps, useFormikContext } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import axios from 'axios';
import { ICreateCheckRequest } from '@modules/accounting/CheckerMenu/interfaces';
import { FormPart } from '@modules/shared/components/FormPart';
import { getOptions } from '@shared/commonFunctions';
import { SERVICES } from '@shared/constants/apiConfig';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { TextInputField } from '@shared/fields/TextInputField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { IOption } from '@shared/interfaces';
import { CommonButtonWrapper } from '@styles/CommonStyles';

import { NContact } from '../../Contact/validation';
import { DealContext } from '../DealContext/DealContext';
import { CheckRequestType, IProduct, IProductsFetch } from '../interfaces';
import { ProductStatus } from './Products/constants';
import { IDealInfo } from './CheckRequest/CheckRequestModal';

const REASON_OPTIONS = [
  {
    value: 'Tax/Title Fee',
    label: 'Tax/Title Fee',
  },
  {
    value: 'Expected Titling Fees',
    label: 'Expected Titling Fees',
  },
  {
    value: 'Duplicate Title Fee',
    label: 'Duplicate Title Fee',
  },
  {
    value: 'Short Payoff',
    label: 'Short Payoff',
  },
  {
    value: 'Additional Tax/Title Fees',
    label: 'Additional Tax/Title Fees',
  },
  {
    value: 'DMV Inspections',
    label: 'DMV Inspections',
  },
  {
    value: 'Registration Fees',
    label: 'Registration Fees',
  },
  {
    value: 'Toll Violations',
    label: 'Toll Violations',
  },
  {
    value: '2nd Lien Payoff',
    label: '2nd Lien Payoff',
  },
  {
    value: 'Child Support Lien',
    label: 'Child Support Lien',
  },
  {
    value: 'Other',
    label: 'Other',
  }
];

export const GAP_PRODUCT_TYPE = 'Gap';

export const LOAN_PAID_OFF = 'Loan Paid Off';

export interface IContactPCRF {
  firstName: string;
  lastName: string;
  addresses: NContact.Address[];
}

interface IDropdownOption {
	label: string;
	value: string;
}

interface ICreateCheckRequestModalProps extends Partial<FormikProps<ICreateCheckRequest>> {
  handleClose: () => void;
  dealInfo?: IDealInfo;
  contact?: IContactPCRF;
  userName: string | undefined;
  checkRequestType: CheckRequestType;
  dealData?: IProductsFetch;
  payeeList?: IDropdownOption[];
  resaonList?: IOption<unknown>[];
  loading?: boolean;
}

export const CheckRequestForm = ({
  handleClose,
  dealInfo,
  contact,
  userName,
  checkRequestType,
  dealData,
  payeeList,
  resaonList,
  setFieldValue,
  loading,
}: ICreateCheckRequestModalProps) => {
  const [contractedDealsProducts, setContractedDealsProducts] = useState<IProduct[]>([]);
  const { data: payeeData = [],  mutate: reloadPayee} = useSWR(`/${SERVICES.ACCOUNTING}/checkPayee`);
  const { contextValue } = useContext(DealContext);
  const { values } = useFormikContext<ICreateCheckRequest>();

  useMemo(() => {
    if (dealData?.dealsProducts) {
      const productsArr: IProduct[] = [];
      dealData?.dealsProducts.forEach((product: IProduct) => {
        if (product.status === ProductStatus.CONTRACTED) {
          productsArr.push(product);
        }
      });
      setContractedDealsProducts(productsArr);
    }
  }, [dealData]);

  useEffect(() => {
    const isGapLoanPaidOff =
      values?.productType === GAP_PRODUCT_TYPE && values?.reason === LOAN_PAID_OFF;
  
    if (isGapLoanPaidOff && dealInfo?.lenderResponse && setFieldValue) {
      const paidOffInfo = dealInfo?.gapLoanPaidOffRequestInformation;
  
      if (paidOffInfo?.payeeName && paidOffInfo?.payeeAddress) {
        const formattedPayee = `${paidOffInfo.payeeName} ${paidOffInfo.payeeAddress}`;
        setFieldValue('payeeName', formattedPayee);
      }
    }
  }, [values?.productType, values?.reason, dealData, dealInfo, setFieldValue]);

  const payeeNameChange = useCallback(async(selectedValue?: string) => {
    const payeeName = payeeData.find((a: { payeeName: string | undefined; }) => a.payeeName === selectedValue);
    if (!payeeName) {
      await axios.post(`/${SERVICES.ACCOUNTING}/checkPayee`, {payeeName: selectedValue});
      reloadPayee();
    }
  }, [payeeData]);
   
  const findProduct = useCallback(
    (selectedProduct: string) => {
      const providerName =
        dealData?.dealsProducts?.find((product) => product.productTypeName === selectedProduct)?.providerName || '';
      setFieldValue?.('provider', providerName);
    },
    [dealData]
  );

  const titleCheckRequestForm = [
    {
      name: 'requestedBy',
      width: '25%',
      value: userName,
      minWidth: '200px',
    },
    {
      name: 'customerName',
      label: 'Customer Name',
      value: `${contact?.firstName} ${contact?.lastName}`,
      width: '25%',
      minWidth: '200px',
    },
    {
      name: 'loanConsultant',
      label: 'Assigned LC',
      width: '25%',
      value: contextValue?.Lc,
      minWidth: '120px',
    },
    {
      name: 'payeeName',
      width: '25%',
      minWidth: '80px',
      options: getOptions(payeeData, 'payeeName', 'payeeName'),
      component: SelectAutocompleteField,
      onChange: payeeNameChange,
      isCreatable: true
    },
    {
      name: 'amountRequested',
      label: 'Amount Requested',
      width: '25%',
      minWidth: '200px',
      prefix: '$',
      decimalScale: 2,
      component: NumberInputField,
    },
    {
      name: 'reason',
      label: 'Reason',
      width: '25%',
      minWidth: '80px',
      component: SelectAutocompleteField,
      options: REASON_OPTIONS,
    },
    {
      name: 'relatedDeal',
      label: 'Related Deal',
      width: '25%',
      minWidth: '80px',
      value: `${dealInfo?.dealId} ${dealInfo?.assetTag}`,
    },
    {
      name: 'includeInBatch',
      width: '25%',
      minWidth: '80px',
      component: CheckboxField,
    },
    {
      width: '100%',
      name: 'requestComment',
      label: 'Request Comment',
      multiline: true,
      minRows: 7,
      component: TextInputField,
    },
  ];

  const productCheckRequestForm = useMemo(
    () => [
      {
        name: 'productType',
        width: '25%',
        minWidth: '80px',
        options: getOptions(contractedDealsProducts, 'productTypeName', 'productTypeName'),
        onChange: findProduct,
        component: SelectAutocompleteField,
      },
      {
        name: 'payeeName',
        width: '25%',
        minWidth: '80px',
        options: payeeList,
        component: SelectAutocompleteField,
        onChange: payeeNameChange
      },
      {
        name: 'provider',
        width: '25%',
        minWidth: '80px',
        component: TextInputField,
      },
      {
        name: 'lenderCode',
        width: '25%',
        minWidth: '80px',
        disabled: true,
        component: TextInputField,
      },
      {
        name: 'customerAmountRefund',
        label: 'Customer Amount Refund',
        width: '25%',
        minWidth: '200px',
        prefix: '$',
        decimalScale: 2,
        component: NumberInputField,
      },
      {
        name: 'sellerAmountRefund',
        label: 'Seller Amount Refund',
        width: '25%',
        minWidth: '200px',
        prefix: '$',
        decimalScale: 2,
        component: NumberInputField,
      },
      {
        name: 'amountRequested',
        label: 'Amount Requested',
        width: '25%',
        minWidth: '200px',
        prefix: '$',
        decimalScale: 2,
        component: NumberInputField,
      },
      {
        name: 'cancelledDate',
        width: '25%',
        minWidth: '200px',
        component: SelectDateField,
      },
      {
        name: 'achRefNumber',
        width: '25%',
        minWidth: '200px',
        component: TextInputField,
      },
      {
        name: 'relatedDeal',
        label: 'Related Deal',
        width: '25%',
        minWidth: '80px',
      },
      {
        name: 'reason',
        label: 'Reason for Cancellation',
        width: '25%',
        minWidth: '80px',
        component: SelectAutocompleteField,
        options: resaonList,
      },
    ],
    [dealInfo, contractedDealsProducts, resaonList]
  );

  return (
    <>
      <FormPart
        name={checkRequestType === CheckRequestType.CheckRequest ? 'Create Check Request' : 'Create Product Cancellation Checks'}
        config={checkRequestType === CheckRequestType.CheckRequest ? titleCheckRequestForm : productCheckRequestForm}
      />

      <CommonButtonWrapper>
        <CancelButton
          width="60px"
          onClick={handleClose}
        />
        <SaveButton
          width="60px"
          disabled={checkRequestType === CheckRequestType.ProductCancelCheck && contractedDealsProducts?.length <= 0}
          isLoading={loading}
        />
      </CommonButtonWrapper>
    </>
  );
};
